import { agentDb } from "@/lib/firebase";
import { HermesService } from "@/lib/hermesService";
import {
  AssistantActionEntity,
  ConversationActor,
  ConversationContentBlock,
  ConversationMessage,
  ConversationThread,
  safeConversationMessageParse,
  safeConversationThreadParse,
} from "@/schemas/firestore/conversations.schema";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { StateCreator } from "zustand";
import type { StoreState } from "..";

/* ---------- UI shapes ---------- */

export interface AssistantMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** Agent working notes (collapsed "Working…" panel), resolved from the
   * final message's display="raw" sibling via meta.parent_message_id. */
  trace: string | null;
  created_at: string;
  action_entities: AssistantActionEntity[];
  /** Who produced this message — the whole point of stamping. Threads are
   * shared across the org, so a transcript is only readable if each turn
   * says which member it came from. */
  author: ConversationActor;
}

/**
 * Local overlay for the in-flight turn. The worker persists message docs
 * only AFTER the SSE stream ends (finalise_turn runs post-stream), so the
 * overlay must survive [DONE] and clear when the subscription delivers the
 * turn's final doc.
 */
export interface PendingTurn {
  user_text: string;
  trace: string;
  final: string;
  /** The user message doc reached Firestore — hide the overlay's user bubble. */
  is_user_persisted: boolean;
  is_failed: boolean;
}

const blocksToText = (blocks: ConversationContentBlock[]): string =>
  blocks
    .filter((b) => b.type === "text" && typeof b.text === "string")
    .map((b) => b.text)
    .join("");

/** Thread titles are set-once at creation (the worker has no rename
 * endpoint), so threads are created lazily on first send and titled from
 * that message. */
const deriveThreadTitle = (text: string): string =>
  text.trim().replace(/\s+/g, " ").slice(0, 60);

/**
 * Order a thread by causality rather than wall-clock. An assistant message's
 * `created_at` is stamped when the turn *finished*, so a message sent during a
 * long turn carries an earlier timestamp than the reply that logically precedes
 * it, and Firestore's `orderBy("created_at")` genuinely interleaves the two
 * turns wrongly. A reply therefore sorts by its question
 * (`meta.source_message_id`); on a tie the sort is stable, so the incoming
 * `created_at` order keeps the question above its reply.
 */
const orderingKey = (
  m: ConversationMessage,
  createdAtById: Map<string, number>,
): number => {
  const own = createdAtById.get(m.message_id) ?? 0;
  const sourceId = m.meta.source_message_id;
  return typeof sourceId === "string"
    ? (createdAtById.get(sourceId) ?? own)
    : own;
};

/**
 * Each assistant turn persists twice server-side: display="raw" (verbatim
 * transcript) and display="final" (narrator answer). Render user messages
 * and final assistant messages; attach each final's raw sibling text as
 * its trace.
 */
export const toAssistantMessages = (
  messages: ConversationMessage[],
): AssistantMessage[] => {
  const rawTextById = new Map<string, string>();
  const createdAtById = new Map<string, number>();
  messages.forEach((m) => {
    createdAtById.set(m.message_id, m.created_at?.toDate().getTime() ?? 0);
    if (m.role === "assistant" && m.display === "raw") {
      rawTextById.set(m.message_id, blocksToText(m.content_blocks));
    }
  });
  return messages
    .filter(
      (m): m is ConversationMessage & { role: "user" | "assistant" } =>
        m.role === "user" || (m.role === "assistant" && m.display === "final"),
    )
    .sort(
      (a, b) => orderingKey(a, createdAtById) - orderingKey(b, createdAtById),
    )
    .map((m) => {
      const parentId = m.meta.parent_message_id;
      return {
        id: m.message_id,
        role: m.role,
        text: blocksToText(m.content_blocks),
        trace:
          m.role === "assistant" && typeof parentId === "string"
            ? (rawTextById.get(parentId) ?? null)
            : null,
        created_at: m.created_at?.toDate().toISOString() ?? "",
        action_entities:
          m.role === "assistant" ? (m.meta.action_entities ?? []) : [],
        // The final message's author; its raw sibling is folded into `trace`
        // and carries the same actor.
        author: m.author,
      };
    });
};

/* ---------- slice ---------- */

export interface AssistantSlice {
  /* state */
  assistantThreads: ConversationThread[];
  currentThreadId: string | null;
  assistantMessages: AssistantMessage[];
  pendingTurn: PendingTurn | null;
  is_streaming: boolean;
  is_loading_threads: boolean;
  assistantError: string | null;

  /* actions */
  subscribeToAssistantThreads: (domainId: string) => () => void;
  createAssistantThread: (title?: string) => Promise<string>;
  selectAssistantThread: (threadId: string) => void;
  /** Switch to an empty draft state; the thread is created on first send. */
  startNewAssistantThread: () => void;
  sendAssistantTurn: (
    text: string,
    attachmentDocIds?: string[],
  ) => Promise<void>;
  archiveAssistantThread: (threadId: string) => Promise<void>;
  resetAssistant: () => void;

  /* internals */
  _attachAssistantMessagesListener: (threadId: string) => void;
  _assistantAbort: AbortController | null;
  _assistantThreadsUnsubs: Map<string, () => void>;
  _assistantMessagesUnsub: (() => void) | null;
  /** Rendered message ids at the moment the in-flight turn started; a new
   * final doc id outside this set means the turn's persistence landed. */
  _assistantSeenMessageIds: Set<string> | null;
}

export const createAssistantSlice: StateCreator<
  StoreState,
  [],
  [],
  AssistantSlice
> = (set, get) => ({
  /* ---------- initial state ---------- */
  assistantThreads: [],
  currentThreadId: null,
  assistantMessages: [],
  pendingTurn: null,
  is_streaming: false,
  is_loading_threads: false,
  assistantError: null,
  _assistantAbort: null,
  _assistantThreadsUnsubs: new Map(),
  _assistantMessagesUnsub: null,
  _assistantSeenMessageIds: null,

  /* ---------- actions ---------- */
  subscribeToAssistantThreads: (domainId) => {
    const orgId = get().orgId;
    if (!orgId || !domainId) {
      console.log(
        "Assistant threads subscription requires orgId and domainId.",
      );
      return () => {};
    }

    const existing = get()._assistantThreadsUnsubs.get(domainId);
    if (existing) return existing;

    set((state) => ({
      is_loading_threads: state.assistantThreads.length === 0,
      assistantError: null,
    }));

    const threadsRef = collection(
      agentDb,
      `organizations/${orgId}/domains/${domainId}/conversations`,
    );
    const q = query(threadsRef, orderBy("updated_at", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const threads: ConversationThread[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const result = safeConversationThreadParse({
            ...docSnapshot.data(),
            thread_id: docSnapshot.id,
          });
          if (!result.success) {
            console.error(
              `Validation failed for conversation thread ${docSnapshot.id}:`,
              result.error.issues,
            );
            return;
          }
          // Client-side filter keeps the query on the automatic
          // single-field index (no composite status+updated_at index).
          if (result.data.status !== "archived") {
            threads.push(result.data);
          }
        });
        set({ assistantThreads: threads, is_loading_threads: false });
      },
      (error) => {
        console.error("Error in conversations onSnapshot:", error);
        set({
          assistantError: "Failed to listen to conversations",
          is_loading_threads: false,
        });
      },
    );

    const wrappedUnsubscribe = () => {
      unsubscribe();
      get()._assistantThreadsUnsubs.delete(domainId);
    };
    get()._assistantThreadsUnsubs.set(domainId, wrappedUnsubscribe);
    return wrappedUnsubscribe;
  },

  createAssistantThread: async (title) => {
    const domainId = get().currentDomain?.id;
    if (!domainId) throw new Error("No domain selected");
    try {
      const thread = await HermesService.createThread(domainId, title);
      // Keep any pendingTurn — the first send creates the thread and its
      // overlay must survive the selection. The threads listener picks the
      // new doc up on its own.
      set({ currentThreadId: thread.thread_id, assistantMessages: [] });
      get()._attachAssistantMessagesListener(thread.thread_id);
      return thread.thread_id;
    } catch (error) {
      console.error("Failed to create assistant thread:", error);
      set({ assistantError: "Failed to create conversation" });
      throw error;
    }
  },

  selectAssistantThread: (threadId) => {
    const domainId = get().currentDomain?.id;
    const orgId = get().orgId;
    if (!domainId || !orgId) return;

    set({
      currentThreadId: threadId,
      assistantMessages: [],
      pendingTurn: null,
      assistantError: null,
      _assistantSeenMessageIds: null,
    });
    get()._attachAssistantMessagesListener(threadId);
  },

  startNewAssistantThread: () => {
    get()._assistantMessagesUnsub?.();
    set({
      currentThreadId: null,
      assistantMessages: [],
      pendingTurn: null,
      assistantError: null,
      _assistantMessagesUnsub: null,
      _assistantSeenMessageIds: null,
    });
  },

  _attachAssistantMessagesListener: (threadId) => {
    const domainId = get().currentDomain?.id;
    const orgId = get().orgId;
    if (!domainId || !orgId) return;

    get()._assistantMessagesUnsub?.();

    const messagesRef = collection(
      agentDb,
      `organizations/${orgId}/domains/${domainId}/conversations/${threadId}/messages`,
    );
    const q = query(messagesRef, orderBy("created_at", "asc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // User may have switched threads while this listener was attached.
        if (get().currentThreadId !== threadId) return;

        const parsed: ConversationMessage[] = [];
        snapshot.docs.forEach((docSnapshot) => {
          const result = safeConversationMessageParse({
            ...docSnapshot.data(),
            message_id: docSnapshot.id,
          });
          if (result.success) {
            parsed.push(result.data);
          } else {
            console.error(
              `Validation failed for conversation message ${docSnapshot.id}:`,
              result.error.issues,
            );
          }
        });

        set((state) => {
          let pendingTurn = state.pendingTurn;
          const seen = state._assistantSeenMessageIds;
          if (pendingTurn && seen) {
            const hasNewFinal = parsed.some(
              (m) =>
                m.role === "assistant" &&
                m.display === "final" &&
                !seen.has(m.message_id),
            );
            const hasNewUser = parsed.some(
              (m) => m.role === "user" && !seen.has(m.message_id),
            );
            if (hasNewFinal) {
              // The turn's persistence landed — the docs now carry the
              // canonical answer; drop the overlay.
              pendingTurn = null;
            } else if (hasNewUser && !pendingTurn.is_user_persisted) {
              pendingTurn = { ...pendingTurn, is_user_persisted: true };
            }
          }
          return {
            assistantMessages: toAssistantMessages(parsed),
            pendingTurn,
            ...(pendingTurn === null ? { _assistantSeenMessageIds: null } : {}),
          };
        });
      },
      (error) => {
        console.error("Error in conversation messages onSnapshot:", error);
        set({ assistantError: "Failed to listen to conversation" });
      },
    );

    set({ _assistantMessagesUnsub: unsubscribe });
  },

  sendAssistantTurn: async (text, attachmentDocIds = []) => {
    const domainId = get().currentDomain?.id;
    if (!domainId) throw new Error("No domain selected");
    if (get().is_streaming) return;

    const abort = new AbortController();
    // Overlay goes up before any network call — thread creation on a cold
    // worker can take seconds.
    set((state) => ({
      pendingTurn: {
        user_text: text,
        trace: "",
        final: "",
        is_user_persisted: false,
        is_failed: false,
      },
      is_streaming: true,
      assistantError: null,
      _assistantAbort: abort,
      _assistantSeenMessageIds: new Set(
        state.assistantMessages.map((m) => m.id),
      ),
    }));

    try {
      let threadId = get().currentThreadId;
      if (!threadId) {
        threadId = await get().createAssistantThread(deriveThreadTitle(text));
      }
      await HermesService.streamTurn(
        domainId,
        threadId,
        text,
        {
          // The overlay may already be cleared (doc landed mid-stream) —
          // late chunks are then no-ops.
          onTrace: (chunk) =>
            set((state) =>
              state.pendingTurn
                ? {
                    pendingTurn: {
                      ...state.pendingTurn,
                      trace: state.pendingTurn.trace + chunk,
                    },
                  }
                : {},
            ),
          onFinal: (chunk) =>
            set((state) =>
              state.pendingTurn
                ? {
                    pendingTurn: {
                      ...state.pendingTurn,
                      final: state.pendingTurn.final + chunk,
                    },
                  }
                : {},
            ),
        },
        abort.signal,
        attachmentDocIds,
      );
      // [DONE]: the stream is over but the worker persists the message docs
      // afterwards — keep pendingTurn until the subscription clears it.
      set({ is_streaming: false, _assistantAbort: null });
    } catch (error) {
      if (abort.signal.aborted) {
        // Deliberate cancel (unmount / domain switch) — not an error.
        set({
          pendingTurn: null,
          is_streaming: false,
          _assistantAbort: null,
          _assistantSeenMessageIds: null,
        });
        return;
      }
      console.error("Assistant turn failed:", error);
      // Keep _assistantSeenMessageIds: if the server actually completed the
      // turn (client-side drop), the arriving final doc still clears the
      // failed overlay instead of duplicating the message.
      set((state) => ({
        pendingTurn: state.pendingTurn
          ? { ...state.pendingTurn, is_failed: true }
          : null,
        is_streaming: false,
        assistantError:
          error instanceof Error ? error.message : "Message failed",
        _assistantAbort: null,
      }));
      throw error;
    }
  },

  archiveAssistantThread: async (threadId) => {
    const domainId = get().currentDomain?.id;
    if (!domainId) return;
    try {
      await HermesService.archiveThread(domainId, threadId);
      // The threads listener drops it (status flips to "archived").
      if (get().currentThreadId === threadId) {
        get()._assistantMessagesUnsub?.();
        set({
          currentThreadId: null,
          assistantMessages: [],
          pendingTurn: null,
          _assistantMessagesUnsub: null,
          _assistantSeenMessageIds: null,
        });
      }
    } catch (error) {
      console.error("Failed to archive assistant thread:", error);
      set({ assistantError: "Failed to archive conversation" });
    }
  },

  resetAssistant: () => {
    get()._assistantAbort?.abort();
    get()._assistantMessagesUnsub?.();
    [...get()._assistantThreadsUnsubs.values()].forEach((unsub) => unsub());
    set({
      assistantThreads: [],
      currentThreadId: null,
      assistantMessages: [],
      pendingTurn: null,
      is_streaming: false,
      is_loading_threads: false,
      assistantError: null,
      _assistantAbort: null,
      _assistantThreadsUnsubs: new Map(),
      _assistantMessagesUnsub: null,
      _assistantSeenMessageIds: null,
    });
  },
});
